import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { confirmDialog } from "../lib/confirm";
import { useI18n } from "../lib/i18n";
import { useGuestGate } from "../lib/useGuestGate";
import { BRAND_COLORS, STATUS_COLORS } from "../lib/bookingStatus";
import { bumpUnreadQuotes } from "../lib/unreadQuotes";
import CustomerPinPromptDialog from "../components/customer/CustomerPinPromptDialog";
import { deDate, deDateTime } from "../lib/dateFormat";
import { deCurrency } from "../lib/numberFormat";

function StatusPill({ status, de }) {
  const map = {
    sent:     { label: de ? "Neu" : "New",           color: BRAND_COLORS.gold },
    viewed:   { label: de ? "Angesehen" : "Viewed",  color: BRAND_COLORS.navy },
    approved: { label: de ? "Genehmigt" : "Approved", color: BRAND_COLORS.greenDeep },
    declined: { label: de ? "Abgelehnt" : "Declined", color: STATUS_COLORS.red },
  };
  const s = map[status] || { label: status, color: BRAND_COLORS.navy };
  return (
    <span data-testid={`quote-status-${status}`}
      className="inline-flex items-center gap-2 px-3 py-1 text-[10.5px] tracking-[0.2em] uppercase border"
      style={{ borderColor: `${s.color}40`, color: s.color }}>
      <span className="w-1.5 h-1.5" style={{ background: s.color }} />{s.label}
    </span>
  );
}

export default function CustomerQuotes() {
  const { lang } = useI18n();
  const de = lang === "de";
  const gate = useGuestGate();
  const fmt = (n, cur = "EUR") => deCurrency(n || 0, cur);

  const [rows, setRows] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Feb 2026 · User-Directive „Customer-PIN":
  // Entscheidungs-Bestätigung nur mit PIN. Wir laden den Status einmal
  // beim Öffnen der Seite; die eigentliche Verifikation läuft aber im
  // decide-Endpoint (Server-of-truth).
  const [pinIsSet, setPinIsSet] = useState(null);   // null = unbekannt
  const [pending, setPending] = useState(null);     // { quoteId, decision }
  const [pinMode, setPinMode] = useState(null);     // "confirm" | "setup"

  const loadPinStatus = async () => {
    try {
      const { data } = await api.get("/me/customer-pin/status");
      setPinIsSet(!!data.is_set);
    } catch (e) { setPinIsSet(false); }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/quotes");
      setRows(data);
    } catch (e) { console.error("CustomerQuotes load failed:", e); toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => {
    (async () => {
      // Feb 2026 · User-Directive „Roter Badge = Anzahl offener Angebote":
      // Wir markieren geöffnete Angebote weiterhin als „viewed" (für
      // interne Metriken · Timestamp-Audit), der Badge zählt aber jetzt
      // beide Status `sent` + `viewed`, damit der rote Punkt so lange
      // bleibt, bis der Kunde entschieden hat. `bumpUnreadQuotes` löst
      // ein Refresh aus, ohne auf den 30-s-Poll zu warten.
      try {
        await api.post("/quotes/mark-all-viewed");
        bumpUnreadQuotes();
      } catch (e) {
        console.debug("mark-all-viewed skipped:", e?.response?.status);
      }
      await Promise.all([load(), loadPinStatus()]);
    })();
  }, []);

  const openQuote = async (q) => {
    setOpenId(q.id);
    setNote("");
    if (q.status === "sent") {
      try { await api.post(`/quotes/${q.id}/view`); } catch (e) { console.debug("quote view mark failed:", e); }
      load();
    }
  };

  // Feb 2026 · Vor der eigentlichen Entscheidung: Confirm-Dialog (bei
  // Ablehnung) → PIN-Prompt (oder Setup-Modal, wenn noch keine PIN).
  const decide = async (id, decision) => {
    if (gate.isGuest) { gate.promptUpgrade(); return; }
    if (decision === "declined") {
      if (!(await confirmDialog({
        title: de ? "Angebot ablehnen?" : "Decline quote?",
        description: de
          ? "Das Team wird über Ihre Ablehnung informiert. Sie können jederzeit ein neues Angebot anfordern."
          : "The team will be notified of your decision. You can request a new quote anytime.",
        confirmText: de ? "Ablehnen" : "Decline",
        variant: "danger",
      }))) return;
    }
    setPending({ quoteId: id, decision });
    setPinMode(pinIsSet ? "confirm" : "setup");
  };

  const submitDecision = async (pin) => {
    if (!pending) return;
    setSubmitting(true);
    try {
      await api.post(`/quotes/${pending.quoteId}/decide`, {
        decision: pending.decision,
        note: note || null,
        pin,
      });
      toast.success(pending.decision === "approved"
        ? (de ? "Angebot genehmigt." : "Quote approved.")
        : (de ? "Angebot abgelehnt." : "Quote declined."));
      setPending(null);
      setPinMode(null);
      setOpenId(null);
      // Feb 2026 · User-Directive „Roter Badge": Nach der Entscheidung
      // Badge sofort neu ziehen — die eben entschiedene Zeile fällt aus
      // dem Zähler heraus, der Punkt schrumpft oder verschwindet.
      bumpUnreadQuotes();
      await load();
    } catch (e) {
      // Sonderfall 428 · Backend hat festgestellt, dass keine PIN
      // gesetzt ist (kann bei Race-Condition passieren). Setup-Modal
      // öffnen, den PIN-State aktualisieren.
      if (e?.response?.status === 428) {
        toast.info(de
          ? "Bitte legen Sie zuerst eine 4-stellige PIN an."
          : "Please set up a 4-digit PIN first.");
        setPinIsSet(false);
        setPinMode("setup");
      } else {
        // Bei falscher PIN bleibt der PIN-Modal geöffnet, damit der
        // Kunde direkt neu eingeben kann.
        throw e;
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onPinSetupSuccess = () => {
    // PIN wurde erfolgreich neu eingerichtet — jetzt fragen wir sie
    // direkt für die anstehende Entscheidung ab.
    setPinIsSet(true);
    setPinMode(pending ? "confirm" : null);
  };

  const open = rows.find((r) => r.id === openId);

  return (
    <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-10 md:py-14" data-testid="customer-quotes">
      <div className="mb-10">
        <div className="label-caps mb-3">{de ? "Ihre Angebote" : "Your quotes"}</div>
        <h1 className="font-display text-4xl md:text-5xl text-navy">
          {de ? "Angebote & Freigaben" : "Quotes & Approvals"}
        </h1>
        <p className="text-[15px] text-navy-dark/70 mt-3 max-w-2xl">
          {de
            ? "Prüfen Sie unsere Kostenvoranschläge und geben Sie sie per Klick frei — Zeitstempel wird automatisch dokumentiert."
            : "Review our cost estimates and approve them with a click — the timestamp is documented automatically."}
        </p>
      </div>

      {rows.length === 0 ? (
        <div className="bg-white border border-navy/10 p-16 text-center">
          <div className="font-display text-2xl text-navy mb-2">
            {de ? "Keine Angebote" : "No quotes"}
          </div>
          <p className="text-[13px] text-navy-dark/60">
            {de
              ? "Sobald wir ein Angebot für Sie erstellen, erscheint es hier."
              : "As soon as we prepare a quote for you it will appear here."}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {rows.map((q) => (
            <div key={q.id} data-testid={`quote-row-${q.id}`} className="bg-white border border-navy/10">
              <button onClick={() => openQuote(q)} className="w-full text-left p-6 md:p-7 hover:bg-navy/[0.02] transition-colors">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="label-caps mb-2">{q.property_name} · {deDate(q.created_at)}</div>
                    <h3 className="font-display text-2xl text-navy leading-tight">{q.title}</h3>
                    {q.type === "forwarded" && (
                      <div className="mt-1 text-[11px] tracking-[0.18em] uppercase text-gold" data-testid={`quote-forwarded-hint-${q.id}`}>
                        {de ? "Weitergeleitetes Angebot" : "Forwarded quote"}
                      </div>
                    )}
                    {q.request_title && (
                      <div
                        className="mt-1 text-[12px] text-navy-dark/60 italic"
                        data-testid={`quote-request-link-${q.id}`}
                      >
                        {de ? "Zu Ihrer Anfrage" : "For your request"}: „{q.request_title}"
                      </div>
                    )}
                    <div className="mt-2 font-display text-xl text-navy">{q.type === "forwarded" ? (de ? "PDF-Angebot" : "PDF quote") : fmt(q.total, q.currency)}</div>
                  </div>
                  <StatusPill status={q.status} de={de} />
                </div>
              </button>

              {openId === q.id && open && (
                <div className="border-t border-navy/10 p-6 md:p-8" data-testid={`quote-detail-${q.id}`}>
                  {open.type === "forwarded" ? (
                    <div className="mb-6 space-y-4" data-testid={`forwarded-quote-view-${q.id}`}>
                      {open.admin_message && (
                        <div className="border-l-2 border-gold pl-4 py-2 bg-navy/[0.02]">
                          <div className="label-caps mb-1.5">{de ? "Nachricht vom Team" : "Message from the team"}</div>
                          <p className="text-[14px] text-navy-dark leading-relaxed whitespace-pre-line">{open.admin_message}</p>
                        </div>
                      )}
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          data-testid={`quote-open-pdf-${q.id}`}
                          onClick={() => {
                            const base = process.env.REACT_APP_BACKEND_URL || "";
                            window.open(`${base}/api/quotes/${q.id}/pdf`, "_blank", "noopener");
                          }}
                          className="inline-flex items-center gap-2 border border-navy text-navy px-5 py-2.5 text-[11px] tracking-[0.15em] uppercase hover:bg-navy hover:text-ivory transition-colors"
                        >
                          {de ? "PDF öffnen" : "Open PDF"}
                        </button>
                        <div className="text-[12px] text-navy-dark/60">
                          {de ? "Das Angebot der Drittfirma öffnet in einem neuen Tab." : "The third-party quote opens in a new tab."}
                        </div>
                      </div>
                    </div>
                  ) : (
                  <>
                  {open.description && <p className="text-[14px] text-navy-dark leading-relaxed mb-6">{open.description}</p>}
                  <div className="border border-navy/10 mb-6">
                    <div className="grid grid-cols-12 gap-2 p-3 label-caps text-[10.5px] border-b border-navy/10 bg-navy/[0.02]">
                      <div className="col-span-6">{de ? "Position" : "Item"}</div>
                      <div className="col-span-2 text-right">{de ? "Menge" : "Qty"}</div>
                      <div className="col-span-2 text-right">{de ? "Einzelpreis" : "Unit price"}</div>
                      <div className="col-span-2 text-right">{de ? "Summe" : "Total"}</div>
                    </div>
                    {open.line_items.map((li, i) => (
                      <div key={i} className="grid grid-cols-12 gap-2 p-3 text-[13px] border-b border-navy/5 last:border-b-0">
                        <div className="col-span-6 text-navy">{li.label}</div>
                        <div className="col-span-2 text-right text-navy-dark">{li.qty} {li.unit || ""}</div>
                        <div className="col-span-2 text-right text-navy-dark">{fmt(li.unit_price, open.currency)}</div>
                        <div className="col-span-2 text-right text-navy font-medium">{fmt(li.qty * li.unit_price, open.currency)}</div>
                      </div>
                    ))}
                    <div className="grid grid-cols-12 gap-2 p-3 text-[14px] border-t border-navy/20 bg-navy/[0.03]">
                      <div className="col-span-10 text-right label-caps">{de ? "Gesamt" : "Total"}</div>
                      <div className="col-span-2 text-right font-display text-lg text-navy">{fmt(open.total, open.currency)}</div>
                    </div>
                  </div>
                  </>
                  )}
                  {open.valid_until && (
                    <div className="text-[12px] text-navy-dark/60 mb-4">
                      {de ? "Gültig bis" : "Valid until"}: {deDate(open.valid_until)}
                    </div>
                  )}
                  {(open.status === "sent" || open.status === "viewed") ? (
                    <>
                      <textarea rows={2} placeholder={de ? "Anmerkung (optional)" : "Comment (optional)"}
                        data-testid="quote-note-input"
                        value={note} onChange={(e) => setNote(e.target.value)}
                        className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3 mb-4 text-[13px]" />
                      {pinIsSet === false && (
                        <div
                          className="mb-4 p-3 border border-gold/40 bg-gold/[0.08] text-[12px] text-navy leading-relaxed"
                          data-testid="quote-pin-required-hint"
                        >
                          {de
                            ? "Um Angebote zu bestätigen, richten Sie zuerst eine persönliche 4-stellige PIN ein. Diese wird beim Klick auf Genehmigen oder Ablehnen abgefragt."
                            : "Please set up a personal 4-digit PIN before deciding on quotes. It will be requested when you approve or decline."}
                        </div>
                      )}
                      <div className="flex flex-wrap gap-3">
                        <button data-testid={`approve-btn-${q.id}`} data-guest-locked={gate.isGuest ? "1" : "0"} disabled={submitting}
                          onClick={() => decide(q.id, "approved")}
                          className="inline-flex items-center gap-2 text-ivory px-6 py-2.5 text-[12px] tracking-[0.15em] uppercase hover:opacity-90 disabled:opacity-40"
                          style={{ background: BRAND_COLORS.navy }}>
                          {gate.isGuest && <Lock className="w-3.5 h-3.5" strokeWidth={1.6} />}
                          {de ? "Genehmigen" : "Approve"}
                        </button>
                        <button data-testid={`decline-btn-${q.id}`} data-guest-locked={gate.isGuest ? "1" : "0"} disabled={submitting}
                          onClick={() => decide(q.id, "declined")}
                          className="inline-flex items-center gap-2 border border-navy/30 text-navy px-6 py-2.5 text-[12px] tracking-[0.15em] uppercase hover:bg-navy/5 disabled:opacity-40">
                          {gate.isGuest && <Lock className="w-3.5 h-3.5" strokeWidth={1.6} />}
                          {de ? "Ablehnen" : "Decline"}
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="text-[13px] text-navy-dark">
                      <div className="label-caps mb-2">{de ? "Entscheidung" : "Decision"}</div>
                      <div>
                        {open.status === "approved"
                          ? (de ? "Genehmigt" : "Approved")
                          : (de ? "Abgelehnt" : "Declined")}
                        {" "}{de ? "am" : "on"}{" "}
                        {open.decided_at ? deDateTime(open.decided_at) : "—"}
                        {open.decided_by_name ? ` ${de ? "durch" : "by"} ${open.decided_by_name}` : ""}.
                      </div>
                      {open.customer_note && <div className="mt-2 italic text-navy-dark/70">„{open.customer_note}"</div>}
                      {open.pin_verified && (
                        <div className="mt-2 text-[11.5px] tracking-[0.14em] uppercase" style={{ color: BRAND_COLORS.gold }} data-testid={`quote-pin-signed-${q.id}`}>
                          {de ? "Mit PIN digital signiert" : "Digitally signed with PIN"}
                        </div>
                      )}
                      <div className="mt-4">
                        <button
                          type="button"
                          data-testid={`quote-certificate-btn-${q.id}`}
                          onClick={() => {
                            const base = process.env.REACT_APP_BACKEND_URL || "";
                            window.open(`${base}/api/quotes/${q.id}/signature-certificate`, "_blank", "noopener");
                          }}
                          className="inline-flex items-center gap-2 border border-gold text-gold px-5 py-2.5 text-[11px] tracking-[0.15em] uppercase hover:bg-gold hover:text-ivory transition-colors"
                          title={de ? "Signatur-Zertifikat als PDF öffnen" : "Open signature certificate PDF"}
                        >
                          {de ? "Zertifikat herunterladen" : "Download certificate"}
                        </button>
                        <div className="text-[11.5px] text-navy-dark/55 mt-2 italic">
                          {de
                            ? "Ein einseitiges PDF-Zertifikat für Ihr Archiv — Angebots-Nr., Zeitstempel und PIN-Siegel."
                            : "A one-page PDF certificate for your archive — quote number, timestamp and PIN seal."}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <CustomerPinPromptDialog
        open={!!pinMode && !!pending}
        mode={pinMode || "confirm"}
        de={de}
        title={pinMode === "setup"
          ? (de ? "PIN einrichten" : "Set up PIN")
          : (pending?.decision === "approved"
            ? (de ? "Angebot genehmigen" : "Approve quote")
            : (de ? "Angebot ablehnen" : "Decline quote"))}
        description={pinMode === "setup"
          ? (de
              ? "Bevor Sie Angebote entscheiden können, benötigen Sie eine persönliche 4-stellige PIN. Sie dient als digitale Signatur."
              : "Before you can decide on quotes, set up a personal 4-digit PIN. It serves as your digital signature.")
          : (de
              ? "Bitte bestätigen Sie Ihre Entscheidung mit Ihrer persönlichen 4-stelligen PIN."
              : "Please confirm your decision with your personal 4-digit PIN.")}
        submitLabel={pinMode === "confirm"
          ? (pending?.decision === "approved"
              ? (de ? "Genehmigen" : "Approve")
              : (de ? "Ablehnen" : "Decline"))
          : undefined}
        onConfirm={submitDecision}
        onSuccess={onPinSetupSuccess}
        onClose={() => { if (!submitting) { setPinMode(null); setPending(null); } }}
      />
      {gate.dialog}
    </div>
  );
}
